/**
 * Facturas (invoices) management tab for admin.
 * Lists every generated invoice, lets the admin re-download the PDF and
 * export the full list as CSV for the accountant.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, FileText, Pencil, RefreshCw, Search, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Label } from '../ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InvoicesTab = ({ authHeader }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('active'); // 'active' | 'canceled'

  const load = async (mode = view) => {
    setLoading(true);
    try {
      const url = mode === 'canceled'
        ? `${API}/admin/invoices?canceled=true`
        : `${API}/admin/invoices`;
      const res = await axios.get(url, {
        headers: { Authorization: authHeader },
      });
      setInvoices(res.data.invoices || []);
    } catch (err) {
      toast.error('No se pudieron cargar las facturas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authHeader) load(view);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeader, view]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return invoices;
    return invoices.filter((i) =>
      [i.number, i.customer_name, i.customer_email, i.customer_number]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle))
    );
  }, [invoices, q]);

  const downloadPdf = async (inv) => {
    try {
      const res = await axios.get(`${API}/admin/invoices/${inv.id}/pdf`, {
        headers: { Authorization: authHeader },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Error al descargar la factura');
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/invoices/export.csv`, {
        headers: { Authorization: authHeader },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'facturas_halo.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV descargado');
    } catch {
      toast.error('Error al exportar CSV');
    }
  };

  const deleteInvoice = async (inv) => {
    const ok = window.confirm(
      `¿Eliminar la factura ${inv.number} de ${inv.customer_name || 'el cliente'}?\n\n` +
        'La factura quedará marcada como ANULADA y se enviará una copia ' +
        'al cliente por email. Esta acción no se puede deshacer.'
    );
    if (!ok) return;
    try {
      const res = await axios.delete(`${API}/admin/invoices/${inv.id}`, {
        headers: { Authorization: authHeader },
      });
      if (res.data?.email_sent) {
        toast.success(`Factura ${inv.number} anulada y enviada al cliente`);
      } else {
        toast.success(`Factura ${inv.number} anulada (no se pudo enviar email)`);
      }
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Error al anular la factura';
      toast.error(detail);
    }
  };

  const openEdit = (inv) => {
    setEditing(inv);
    setForm({
      customer_name: inv.customer_name || '',
      customer_email: inv.customer_email || '',
      customer_phone: inv.customer_phone || '',
      customer_dni: inv.customer_dni || '',
      customer_address: inv.customer_address || '',
      service_name: inv.service_name || '',
      amount: inv.amount ?? '',
      deposit_paid: inv.deposit_paid ?? '',
      issued_at: (inv.issued_at || '').slice(0, 10),
    });
  };

  const closeEdit = () => {
    setEditing(null);
    setForm({});
  };

  const saveEdit = async () => {
    if (!editing) return;
    if (!form.customer_name?.trim()) {
      toast.error('El nombre del cliente es obligatorio');
      return;
    }
    if (!form.customer_email?.trim()) {
      toast.error('El email del cliente es obligatorio');
      return;
    }
    const amount = parseFloat(form.amount);
    const deposit = parseFloat(form.deposit_paid);
    if (Number.isNaN(amount) || amount < 0) {
      toast.error('Importe inválido');
      return;
    }
    if (Number.isNaN(deposit) || deposit < 0) {
      toast.error('Depósito inválido');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/invoices/${editing.id}`,
        {
          customer_name: form.customer_name.trim(),
          customer_email: form.customer_email.trim(),
          customer_phone: form.customer_phone.trim(),
          customer_dni: form.customer_dni.trim(),
          customer_address: form.customer_address.trim(),
          service_name: form.service_name.trim(),
          amount,
          deposit_paid: deposit,
          issued_at: form.issued_at,
        },
        { headers: { Authorization: authHeader } }
      );
      if (res.data?.email_sent) {
        toast.success(`Factura ${editing.number} actualizada y reenviada al cliente`);
      } else {
        toast.success(
          `Factura ${editing.number} actualizada (no se pudo enviar email)`
        );
      }
      closeEdit();
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Error al guardar la factura';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const totalAmount = filtered.reduce((acc, i) => acc + (i.amount || 0), 0);
  const isCanceledView = view === 'canceled';

  return (
    <div className="space-y-6" data-testid="invoices-tab">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-heading text-halo-charcoal flex items-center gap-2">
            <FileText size={22} /> Facturas
          </h2>
          <p className="text-sm text-halo-text">
            {filtered.length} {isCanceledView ? 'anuladas' : 'facturas'}
            {!isCanceledView && (
              <> · Total: <strong>{totalAmount.toFixed(2)}€</strong></>
            )}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => load(view)}
            disabled={loading}
            data-testid="invoices-refresh"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
          {!isCanceledView && (
            <Button
              onClick={downloadCsv}
              className="bg-halo-brown hover:bg-halo-charcoal text-halo-cream"
              data-testid="invoices-export-csv"
            >
              <Download size={16} className="mr-2" /> Exportar CSV
            </Button>
          )}
        </div>
      </div>

      <div
        className="inline-flex rounded-lg border border-halo-taupe/20 bg-halo-cream/40 p-1"
        data-testid="invoices-view-toggle"
      >
        <button
          type="button"
          onClick={() => setView('active')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
            !isCanceledView
              ? 'bg-white text-halo-charcoal shadow-sm'
              : 'text-halo-text hover:text-halo-charcoal'
          }`}
          data-testid="invoices-view-active"
        >
          Activas
        </button>
        <button
          type="button"
          onClick={() => setView('canceled')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
            isCanceledView
              ? 'bg-white text-red-600 shadow-sm'
              : 'text-halo-text hover:text-halo-charcoal'
          }`}
          data-testid="invoices-view-canceled"
        >
          Anuladas
        </button>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-3 text-halo-text" />
        <Input
          placeholder="Buscar por nº de factura, cliente o email..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9"
          data-testid="invoices-search"
        />
      </div>

      <div className="bg-white rounded-xl border border-halo-taupe/15 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-halo-cream text-halo-charcoal text-left">
              <tr>
                <th className="px-4 py-3">Nº Factura</th>
                <th className="px-4 py-3">
                  {isCanceledView ? 'Anulada el' : 'Fecha'}
                </th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Servicio</th>
                <th className="px-4 py-3 text-right">Importe</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-halo-text">
                    {loading
                      ? 'Cargando...'
                      : isCanceledView
                        ? 'No hay facturas anuladas.'
                        : 'Aún no hay facturas. Se generan al marcar una reserva como "completada".'}
                  </td>
                </tr>
              )}
              {filtered.map((inv) => (
                <tr
                  key={inv.id}
                  className={`border-t border-halo-taupe/10 hover:bg-halo-cream/30 ${
                    isCanceledView ? 'opacity-70' : ''
                  }`}
                  data-testid={`invoice-row-${inv.number}`}
                >
                  <td className="px-4 py-3 font-mono text-xs">
                    {isCanceledView && (
                      <span className="inline-block mr-1 px-1.5 py-0.5 text-[10px] rounded bg-red-100 text-red-700 font-semibold uppercase tracking-wide">
                        Anulada
                      </span>
                    )}
                    {inv.number}
                  </td>
                  <td className="px-4 py-3">
                    {isCanceledView
                      ? (inv.canceled_at || '').slice(0, 10)
                      : (inv.issued_at || '').slice(0, 10)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{inv.customer_name}</div>
                    <div className="text-xs text-halo-text">{inv.customer_email}</div>
                  </td>
                  <td className="px-4 py-3">{inv.service_name}</td>
                  <td className="px-4 py-3 text-right font-medium">
                    {(inv.amount || 0).toFixed(2)}€
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadPdf(inv)}
                        data-testid={`invoice-pdf-${inv.number}`}
                      >
                        <Download size={14} className="mr-1" /> PDF
                      </Button>
                      {!isCanceledView && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEdit(inv)}
                            data-testid={`invoice-edit-${inv.number}`}
                            title="Editar factura"
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteInvoice(inv)}
                            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                            data-testid={`invoice-delete-${inv.number}`}
                            title="Anular factura y enviar copia al cliente"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && closeEdit()}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="invoice-edit-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              Editar factura {editing?.number}
            </DialogTitle>
            <DialogDescription>
              Al guardar se regenera el PDF con los nuevos datos y se reenvía
              automáticamente al cliente. El número de factura no se modifica.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wide">
                Datos del cliente
              </Label>
            </div>
            <div>
              <Label htmlFor="inv-name">Nombre *</Label>
              <Input
                id="inv-name"
                value={form.customer_name || ''}
                onChange={(e) =>
                  setForm({ ...form, customer_name: e.target.value })
                }
                data-testid="invoice-edit-name"
              />
            </div>
            <div>
              <Label htmlFor="inv-email">Email *</Label>
              <Input
                id="inv-email"
                type="email"
                value={form.customer_email || ''}
                onChange={(e) =>
                  setForm({ ...form, customer_email: e.target.value })
                }
                data-testid="invoice-edit-email"
              />
            </div>
            <div>
              <Label htmlFor="inv-phone">Teléfono</Label>
              <Input
                id="inv-phone"
                value={form.customer_phone || ''}
                onChange={(e) =>
                  setForm({ ...form, customer_phone: e.target.value })
                }
                data-testid="invoice-edit-phone"
              />
            </div>
            <div>
              <Label htmlFor="inv-dni">DNI / NIF</Label>
              <Input
                id="inv-dni"
                value={form.customer_dni || ''}
                onChange={(e) =>
                  setForm({ ...form, customer_dni: e.target.value })
                }
                data-testid="invoice-edit-dni"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="inv-address">Dirección</Label>
              <Input
                id="inv-address"
                value={form.customer_address || ''}
                onChange={(e) =>
                  setForm({ ...form, customer_address: e.target.value })
                }
                data-testid="invoice-edit-address"
              />
            </div>

            <div className="md:col-span-2 pt-2">
              <Label className="text-xs uppercase tracking-wide">
                Servicio e importes
              </Label>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="inv-service">Concepto / Servicio</Label>
              <Input
                id="inv-service"
                value={form.service_name || ''}
                onChange={(e) =>
                  setForm({ ...form, service_name: e.target.value })
                }
                data-testid="invoice-edit-service"
              />
            </div>
            <div>
              <Label htmlFor="inv-amount">Importe total (€) *</Label>
              <Input
                id="inv-amount"
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="invoice-edit-amount"
              />
            </div>
            <div>
              <Label htmlFor="inv-deposit">Depósito pagado (€)</Label>
              <Input
                id="inv-deposit"
                type="number"
                step="0.01"
                min="0"
                value={form.deposit_paid}
                onChange={(e) =>
                  setForm({ ...form, deposit_paid: e.target.value })
                }
                data-testid="invoice-edit-deposit"
              />
            </div>
            <div>
              <Label htmlFor="inv-date">Fecha de emisión</Label>
              <Input
                id="inv-date"
                type="date"
                value={form.issued_at || ''}
                onChange={(e) =>
                  setForm({ ...form, issued_at: e.target.value })
                }
                data-testid="invoice-edit-date"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeEdit}
              disabled={saving}
              data-testid="invoice-edit-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={saveEdit}
              disabled={saving}
              className="bg-halo-brown hover:bg-halo-charcoal text-halo-cream"
              data-testid="invoice-edit-save"
            >
              {saving ? 'Guardando…' : 'Guardar y reenviar al cliente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesTab;
